import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Tooltip,
  Chip,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import axiosInstance from "../../api/axiosInstance";

export default function QuizAdminPanel() {
  const theme = useTheme();
  const [courses, setCourses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter states
  const [selectedSubject, setSelectedSubject] = useState("All");
  const [selectedModule, setSelectedModule] = useState("");

  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  // Upload excel states
  const [uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  // Form states
  const [subject, setSubject] = useState("");
  const [moduleId, setModuleId] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [optionA, setOptionA] = useState("");
  const [optionB, setOptionB] = useState("");
  const [optionC, setOptionC] = useState("");
  const [optionD, setOptionD] = useState("");
  const [correctOption, setCorrectOption] = useState("A");
  const [explanation, setExplanation] = useState("");

  const colors = {
    primary: "#e50914",
    secondary: "#6a0dad",
  };

  // Fetch subjects/courses and questions
  const fetchCourses = async () => {
    try {
      const res = await axiosInstance.get("/course");
      setCourses(res.data);
    } catch (err) {
      console.error("Error fetching courses for admin:", err);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      let url = "/quiz/admin/questions";
      const params = [];
      if (selectedSubject && selectedSubject !== "All") {
        params.push(`subject=${selectedSubject}`);
      }
      if (selectedModule) {
        params.push(`module_id=${selectedModule}`);
      }
      if (params.length > 0) {
        url += `?${params.join("&")}`;
      }

      const res = await axiosInstance.get(url);
      setQuestions(res.data);
    } catch (err) {
      console.error("Error fetching quiz questions for admin:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubject, selectedModule]);

  // Open dialog for adding
  const handleOpenAdd = () => {
    setEditingQuestion(null);
    setSubject(selectedSubject !== "All" ? selectedSubject : (courses[0]?.slug || ""));
    setModuleId(selectedModule || "1");
    setQuestionText("");
    setOptionA("");
    setOptionB("");
    setOptionC("");
    setOptionD("");
    setCorrectOption("A");
    setExplanation("");
    setOpenDialog(true);
  };

  // Open dialog for editing
  const handleOpenEdit = (q) => {
    setEditingQuestion(q);
    setSubject(q.subject);
    setModuleId(q.module_id);
    setQuestionText(q.question_text);
    setOptionA(q.option_a);
    setOptionB(q.option_b);
    setOptionC(q.option_c);
    setOptionD(q.option_d);
    setCorrectOption(q.correct_option);
    setExplanation(q.explanation || "");
    setOpenDialog(true);
  };

  // Delete question
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this question?")) return;
    try {
      await axiosInstance.delete(`/quiz/admin/questions/${id}`);
      fetchQuestions();
    } catch (err) {
      console.error("Error deleting question:", err);
      alert("Failed to delete question.");
    }
  };

  // Submit Form: Add or Edit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !moduleId || !questionText || !optionA || !optionB || !optionC || !optionD || !correctOption) {
      alert("Please fill in all required fields.");
      return;
    }

    const payload = {
      subject,
      module_id: moduleId,
      question_text: questionText,
      option_a: optionA,
      option_b: optionB,
      option_c: optionC,
      option_d: optionD,
      correct_option: correctOption,
      explanation,
    };

    try {
      if (editingQuestion) {
        await axiosInstance.put(`/quiz/admin/questions/${editingQuestion.id}`, payload);
      } else {
        await axiosInstance.post("/quiz/admin/questions", payload);
      }
      setOpenDialog(false);
      fetchQuestions();
    } catch (err) {
      console.error("Error saving question:", err);
      alert("Failed to save question.");
    }
  };

  // Download Excel Template
  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get("/quiz/admin/questions/template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "quiz_questions_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Error downloading template:", err);
      alert("Failed to download template.");
    }
  };

  // Upload Excel File
  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadErrors([]);

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    try {
      const res = await axiosInstance.post("/quiz/admin/questions/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert(res.data.message || "Upload successful!");
      fetchQuestions();
    } catch (err) {
      console.error("Error uploading excel:", err);
      if (err.response && err.response.data && err.response.data.errors) {
        setUploadErrors(err.response.data.errors);
        setOpenUploadDialog(true);
      } else {
        alert(err.response?.data?.detail || "Failed to upload questions.");
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Action Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4, flexWrap: "wrap", gap: 2 }}
      >
        <Typography variant="h5" fontWeight={800}>
          Quiz MCQ Management
        </Typography>

        <Stack direction="row" spacing={2} sx={{ flexWrap: "wrap", gap: 1.5 }}>
          {/* Download Template Button */}
          <Button
            variant="outlined"
            onClick={handleDownloadTemplate}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              borderColor: colors.secondary,
              color: colors.secondary,
              "&:hover": {
                borderColor: colors.secondary,
                background: "rgba(106, 13, 173, 0.05)",
              },
            }}
          >
            Download Template
          </Button>

          {/* Upload Excel Button */}
          <Button
            variant="outlined"
            component="label"
            disabled={uploading}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              borderColor: colors.primary,
              color: colors.primary,
              "&:hover": {
                borderColor: colors.primary,
                background: "rgba(229, 9, 20, 0.05)",
              },
            }}
          >
            {uploading ? <CircularProgress size={20} color="inherit" /> : "Upload Excel"}
            <input
              type="file"
              accept=".xlsx"
              hidden
              onChange={handleUploadExcel}
            />
          </Button>

          {/* Manual Add Button */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenAdd}
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              textTransform: "none",
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              boxShadow: "0 4px 15px rgba(229, 9, 20, 0.2)",
              "&:hover": {
                background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
                transform: "translateY(-1px)",
              },
            }}
          >
            Add MCQ Question
          </Button>
        </Stack>
      </Stack>

      {/* Filters Bar */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: "16px",
          border: "1px solid rgba(255,255,255,0.05)",
          background: "rgba(255,255,255,0.02)",
        }}
      >
        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap" sx={{ gap: 2 }}>
          {/* Subject Filter */}
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="admin-quiz-subject-label">Filter by Subject</InputLabel>
            <Select
              labelId="admin-quiz-subject-label"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              label="Filter by Subject"
              sx={{ borderRadius: "10px" }}
            >
              <MenuItem value="All">All Subjects</MenuItem>
              {courses.map((course) => (
                <MenuItem key={course.id} value={course.slug}>
                  {course.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Module Filter */}
          <TextField
            label="Filter by Module ID"
            variant="outlined"
            size="small"
            value={selectedModule}
            onChange={(e) => setSelectedModule(e.target.value)}
            sx={{
              minWidth: 150,
              "& .MuiOutlinedInput-root": { borderRadius: "10px" },
            }}
            placeholder="e.g. 1"
          />

          <Chip
            label={`${questions.length} Questions Loaded`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 700 }}
          />
        </Stack>
      </Paper>

      {/* Questions List */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : questions.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.05)",
            background: "rgba(255,255,255,0.01)",
          }}
        >
          <Typography color="text.secondary" variant="body1">
            No quiz questions found matching the filter criteria. Start by adding one!
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.05)",
            background: "transparent",
            overflow: "hidden",
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              <TableRow>
                <TableCell style={{ fontWeight: 700 }}>ID</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Subject</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Module</TableCell>
                <TableCell style={{ fontWeight: 700 }} width="40%">Question Text</TableCell>
                <TableCell style={{ fontWeight: 700 }}>Correct</TableCell>
                <TableCell style={{ fontWeight: 700 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questions.map((q) => (
                <TableRow
                  key={q.id}
                  sx={{
                    "&:hover": { backgroundColor: "rgba(255,255,255,0.02)" },
                  }}
                >
                  <TableCell>{q.id}</TableCell>
                  <TableCell>
                    <Chip label={q.subject.toUpperCase()} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>Module {q.module_id}</TableCell>
                  <TableCell>
                    <Tooltip title={q.question_text}>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {q.question_text}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`(${q.correct_option})`}
                      size="small"
                      sx={{
                        backgroundColor: "rgba(0,230,118,0.1)",
                        color: "#00e676",
                        fontWeight: "bold",
                        border: "1px solid rgba(0,230,118,0.3)",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <IconButton color="primary" onClick={() => handleOpenEdit(q)} size="small">
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(q.id)} size="small">
                        <Delete fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add / Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: theme.palette.mode === "dark" ? "rgba(30,30,40,0.95)" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingQuestion ? "Edit MCQ Question" : "Add MCQ Question"}
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={3}>
              <Stack direction="row" spacing={3}>
                {/* Subject Selection */}
                <FormControl fullWidth required size="small">
                  <InputLabel id="dialog-subject-label">Subject</InputLabel>
                  <Select
                    labelId="dialog-subject-label"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    label="Subject"
                  >
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={course.slug}>
                        {course.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {/* Module selection */}
                <TextField
                  label="Module ID"
                  required
                  fullWidth
                  size="small"
                  value={moduleId}
                  onChange={(e) => setModuleId(e.target.value)}
                  placeholder="e.g. 1"
                />
              </Stack>

              {/* Question Text */}
              <TextField
                label="Question Text"
                required
                multiline
                rows={3}
                fullWidth
                value={questionText}
                onChange={(e) => setQuestionText(e.target.value)}
                placeholder="What is the output of...?"
              />

              {/* Option A & B */}
              <Stack direction="row" spacing={3}>
                <TextField
                  label="Option A"
                  required
                  fullWidth
                  size="small"
                  value={optionA}
                  onChange={(e) => setOptionA(e.target.value)}
                />
                <TextField
                  label="Option B"
                  required
                  fullWidth
                  size="small"
                  value={optionB}
                  onChange={(e) => setOptionB(e.target.value)}
                />
              </Stack>

              {/* Option C & D */}
              <Stack direction="row" spacing={3}>
                <TextField
                  label="Option C"
                  required
                  fullWidth
                  size="small"
                  value={optionC}
                  onChange={(e) => setOptionC(e.target.value)}
                />
                <TextField
                  label="Option D"
                  required
                  fullWidth
                  size="small"
                  value={optionD}
                  onChange={(e) => setOptionD(e.target.value)}
                />
              </Stack>

              <Stack direction="row" spacing={3} alignItems="center">
                {/* Correct Option Dropdown */}
                <FormControl required sx={{ minWidth: 150 }} size="small">
                  <InputLabel id="correct-opt-label">Correct Option</InputLabel>
                  <Select
                    labelId="correct-opt-label"
                    value={correctOption}
                    onChange={(e) => setCorrectOption(e.target.value)}
                    label="Correct Option"
                  >
                    <MenuItem value="A">Option A</MenuItem>
                    <MenuItem value="B">Option B</MenuItem>
                    <MenuItem value="C">Option C</MenuItem>
                    <MenuItem value="D">Option D</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="caption" color="text.secondary">
                  Specify the key (A, B, C, or D) corresponding to the correct answer.
                </Typography>
              </Stack>

              {/* Explanation text */}
              <TextField
                label="Explanation (Shown to students post-submission)"
                multiline
                rows={2}
                fullWidth
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                placeholder="Option A is correct because..."
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)} color="inherit" sx={{ fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              borderRadius: "10px",
              fontWeight: 700,
              background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`,
              px: 4,
            }}
          >
            {editingQuestion ? "Save Changes" : "Create Question"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Excel Upload Validation Errors Dialog */}
      <Dialog
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: theme.palette.mode === "dark" ? "rgba(30,30,40,0.95)" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: colors.primary }}>
          Excel Validation Failed
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please correct the following errors in your Excel sheet and try uploading again:
          </Typography>
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            {uploadErrors.map((errorMsg, idx) => (
              <Typography key={idx} variant="body2" color="error" sx={{ py: 0.5, borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                • {errorMsg}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenUploadDialog(false)} variant="contained" sx={{ borderRadius: "10px", fontWeight: 700, px: 3 }}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
